import HeaderBar from "../../components/HeaderBar";
import { useState, useContext, useEffect, useMemo } from "react";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { FaPlus, FaTrash, FaSearch, FaFilter } from "react-icons/fa";

const VehicleTypeMasterListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const API_BASE = "https://nissi-india.com/api/typeMasterListing";

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const [editSelectedIds, setEditSelectedIds] = useState([]);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState([]);

  const normalizeRow = (r) => {
    const id =
      r?.id ??
      r?.vehicleTypeId ??
      r?.vehicletypeid ??
      r?.fld_vehicletypeid ??
      r?.fld_vehicleTypeId ??
      0;

    return {
      ...r,
      id: Number(id) || 0,
      vehicleType:
        r?.vehicleType ??
        r?.vehicletypename ??
        r?.fld_vehicletypename ??
        r?.["Vehicle Type"] ??
        "-",
      vehicleSubType:
        r?.vehicleSubType ??
        r?.vehiclesubtype ??
        r?.fld_vehicle_subtypename ??
        r?.["Vehicle Sub Type"] ??
        "-",
      subAssembly:
        r?.subAssembly ??
        r?.subassembly ??
        r?.fld_subassemblyname ??
        r?.["Vehicle Sub Assembly"] ??
        "-",
      click: r?.click ?? "Click",
    };
  };

  const fetchList = async (searchText = search, pageNo = page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/list`, {
        params: { search: searchText, page: pageNo, limit },
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      const normalized = data.map(normalizeRow).filter((x) => x.id > 0);

      setRows(normalized);
      setTotalPages(res.data?.totalPages || 1);
      setTotal(res.data?.total || 0);

      const tp = res.data?.totalPages || 1;
      if (pageNo > tp) setPage(tp);

      setEditSelectedIds([]);
      setDeleteSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch list!");
      setRows([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, commonHeaders]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleEditSelect = (id) => {
    setEditSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDeleteSelect = (id) => {
    setDeleteSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllEdit = () => {
    if (!rows.length) return;
    if (editSelectedIds.length === rows.length) setEditSelectedIds([]);
    else setEditSelectedIds(rows.map((r) => r.id));
  };

  const toggleSelectAllDelete = () => {
    if (!rows.length) return;
    if (deleteSelectedIds.length === rows.length) setDeleteSelectedIds([]);
    else setDeleteSelectedIds(rows.map((r) => r.id));
  };

  const handleDeleteSelected = async () => {
    if (!deleteSelectedIds.length) {
      toast.warn("Please select at least one record in Delete checkbox!");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/delete`,
        { ids: deleteSelectedIds },
        { headers: commonHeaders }
      );
      toast.success(res.data?.message || "Deleted successfully");
      fetchList(search, page);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed!");
    }
  };

  const handleEditSelected = () => {
    if (!editSelectedIds.length) {
      toast.warn("Please select one record in Edit checkbox!");
      return;
    }
    if (editSelectedIds.length > 1) {
      toast.warn("Please select only one record to edit!");
      return;
    }

    const id = Number(editSelectedIds[0]) || 0;
    if (!id) {
      toast.error("Invalid selected id. Please refresh the page.");
      return;
    }

    navigate(`/vehicle/VehicleTypeMaster?vehicleTypeId=${id}`);
  };

  const handleSearchClick = () => {
    setPage(1);
    fetchList(search, 1);
  };

  const handleClear = () => {
    setSearch("");
    setPage(1);
    fetchList("", 1);
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">TYPE MASTER LISTING</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Type Master
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {total} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate("/vehicle/VehicleTypeMaster")}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
            >
              <FaPlus className="w-4 h-4" />
              Add New
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Search
                </label>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="text-gray-400" />
                  </div>

                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Type to search..."
                    className={`pl-10 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleClear}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
              >
                Clear Search
              </button>

              <button
                onClick={handleSearchClick}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
              >
                <FaSearch />
                Search Records
              </button>
            </div>
          </div>
        )}

        {(editSelectedIds.length > 0 || deleteSelectedIds.length > 0) && (
          <div
            className={`rounded-lg p-4 mb-6 ${
              isDark ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <span className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                  Edit Selected: {editSelectedIds.length}
                </span>
                <span className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                  Delete Selected: {deleteSelectedIds.length}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleEditSelected}
                  className={`px-4 py-2 text-white rounded-lg font-medium ${buttonClass} ${hoverClass}`}
                >
                  Edit
                </button>

                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium bg-red-600 hover:bg-red-700"
                >
                  <FaTrash className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold">S.No</th>

                  <th className="px-4 py-4 text-left font-semibold">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && editSelectedIds.length === rows.length}
                      onChange={toggleSelectAllEdit}
                      className="mr-2"
                    />
                    Edit
                  </th>

                  <th className="px-4 py-4 text-left font-semibold">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && deleteSelectedIds.length === rows.length}
                      onChange={toggleSelectAllDelete}
                      className="mr-2"
                    />
                    Delete
                  </th>

                  <th className="px-4 py-4 text-left font-semibold">Vehicle Type</th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Sub Type</th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Sub Assembly</th>
                  <th className="px-4 py-4 text-left font-semibold">Click</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  Array.from({ length: limit }).map((_, idx) => (
                    <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-3/4" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-1/2" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-1/2" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-8 w-24 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr
                      key={r.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                        isDark ? "bg-gray-900" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {(page - 1) * limit + idx + 1}
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={editSelectedIds.includes(r.id)}
                          onChange={() => toggleEditSelect(r.id)}
                        />
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={deleteSelectedIds.includes(r.id)}
                          onChange={() => toggleDeleteSelect(r.id)}
                        />
                      </td>

                      <td className="px-4 py-3">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {r.vehicleType}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/vehicle/VehicleSubTypeMaster?vehicletypeid=${r.id}`)}
                          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                        >
                          {r.vehicleSubType}
                        </button>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            navigate(`/vehicle/VehicleTypeSubAssemblyMaster?vehicletypeid=${r.id}`)
                          }
                          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                        >
                          {r.subAssembly}
                        </button>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/vehicle/VehicleSubAssembly?vehicletypeid=${r.id}`)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {r.click}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
          <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            Showing{" "}
            <span className="font-semibold">{total === 0 ? 0 : (page - 1) * limit + 1}</span>{" "}
            to <span className="font-semibold">{Math.min(page * limit, total)}</span> of{" "}
            <span className="font-semibold">{total}</span> results
          </div>

          {!loading && total > 0 && (
            <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 font-sans">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`px-4 py-2 text-sm font-medium ${
                  page === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    page === i + 1
                      ? `${buttonClass} text-white`
                      : `hover:bg-${baseColor}-100 text-gray-700 dark:hover:bg-${baseColor}-800 dark:text-gray-200`
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={`px-4 py-2 text-sm font-medium ${
                  page === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMasterListing;